/**
 * Notice when a deploy has put a different web build live, and say so.
 *
 * Every build bakes in its own id (`__TODOS_BUILD__`, web/vite.config.ts) and the
 * server names the one it serves in `hello`. A deploy restarts the server, so every
 * socket reconnects and its first `hello` is where a tab on the old build finds out.
 *
 * It never reloads by itself: whatever the reader is in the middle of — a half-typed
 * comment, a draft, a terminal — is theirs to finish. The topbar shows a Reload
 * button while `reloadReady` is set, and the click is the reload.
 *
 * The restart comes halfway through a deploy (the health check, the runner and
 * the bridge are still to go, and it may yet roll back), so the new build alone
 * is not "done". The server says whether a deploy of todos is running
 * (server/selfDeploy.ts); while it is, or the server does not know yet, the
 * Reload waits and `deploying` puts up a banner instead.
 */
import { computed, ref } from "vue";

declare const __TODOS_BUILD__: string;

/** True once the server is serving a build other than the one this tab runs. */
export const newBuild = ref(false);

/** A deploy of todos is running. Kept through a `null` (a server just restarted
 *  mid-deploy, not knowing yet), so the banner does not flicker off and on. */
export const deploying = ref(false);
/** The server has said whether it is deploying since it last (re)started. */
const deployKnown = ref(false);

/** The deploy is over and a new build is live: time to offer the reload. */
export const reloadReady = computed(() => newBuild.value && deployKnown.value && !deploying.value);

export function setDeploying(value: boolean | null): void {
  deployKnown.value = value !== null;
  if (value !== null) deploying.value = value;
}

export function checkBuild(serverBuild: string | null): void {
  // Dev serves the bundle from Vite, whatever dist/ the server finds lying about.
  if (!import.meta.env.PROD || !serverBuild) return;
  newBuild.value = serverBuild !== __TODOS_BUILD__;
}
